import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, FormLabel, Popper, Switch, TextField, Theme, Typography } from "@mui/material";
import { NullableString, Preferito, ServiceDescriptor } from "../models/ServiceDescriptor";
import { MySixtemaDialogTitle } from "./ContextSelectorDialog";
import { useTranslation } from "react-i18next";
import { CodiceDescrizione, codiceDescrizioneNullValue, User } from "../models";
import ContextIcon from "../svgicons/ContextIcon";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PaletteIcon from '@mui/icons-material/Palette';
import { Loadable, useRecoilValueLoadable, useSetRecoilState } from "recoil";
import { contextDialogAtom } from "../atoms/dialogContext";
import { makeStyles, createStyles } from '@mui/styles'
import { iconsAllSelector } from "../atoms/image";
import CheckIcon from '@mui/icons-material/Check';
import { useLogger } from "../framework/logger";
// import logConstants from "../utils/logConstants";
import { CodiciSelector } from "./CodiciModal";
import { preferitoCardColors, preferitoCardColorsWidth } from "../Constants";

const useStyles = makeStyles((theme: Theme) => createStyles({
    circle: {
        width: '32px',
        height: '32px',
        //mozBorderRadius: '40px',
        //webkitBorderRadius: '40px',
        borderRadius: '50%',
        cursor: 'pointer',
        '&:hover': {
            transform: 'scale(1.2)',
            //border: 'solid 2px #1e88e5' //+ theme.palette?.primary?.main
            boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)'
        }
    },
}))

type SavePreferitoModalPropsType = {
    open: boolean,
    sd?: ServiceDescriptor,
    preferitoInEdit: Preferito,
    context?: NullableString,
    user: User,
    onHide?: () => void,
    onSave: (sd: ServiceDescriptor, descri: NullableString, context: NullableString, preferitoInEdit: Preferito) => void,
    onCheckPreferito: (sd: ServiceDescriptor, context: NullableString) => Promise<{ exists: boolean }>,
    title?: string
}

const SavePreferitoModal = ({ open, onHide, sd, context, user, onSave, onCheckPreferito, preferitoInEdit, title }: SavePreferitoModalPropsType) => {
    const [codiceSelected, setCodiceSelected] = useState<CodiceDescrizione>();
    const [prefDescri, setPrefDescri] = useState<string>('')
    const [prefWithContext, setPrefWithContext] = useState<boolean>(true)
    const [message, setMessage] = useState<string | null>(null)
    const { t } = useTranslation();
    const setOpenDialog = useSetRecoilState(contextDialogAtom)
    const isAzienda = user?.role === 'azienda'
    const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
    const popperRefObject = useRef(null);
    const iconsAll = useRecoilValueLoadable(iconsAllSelector)
    const [openIcons, setOpenIcons] = useState(false);
    const logger = useLogger({ key: 'Preferito' });

    const [preferito, setPreferito] = useState<Preferito>({
        id: 0,
        groupDescriptorId: 0,
        serviceDescriptorId: 0,
        serviceDescriptor: sd!,
        userId: user.name ?? '',
        title: '',
        descriUrl: '',
        contesto: context!,
        contestoDescri: null,
        iconName: '',
        color: '',
        dataUltimoLancio: null,
        url: '',
    })

    useEffect(() => {
        if (preferitoInEdit) {
            // console.log('preferitoIn edit modal onUpdate if preferitoInEdit', preferitoInEdit)
            setPreferito({
                id: preferitoInEdit.id,
                groupDescriptorId: preferitoInEdit.groupDescriptorId,
                serviceDescriptorId: preferitoInEdit.serviceDescriptorId,
                serviceDescriptor: preferitoInEdit.serviceDescriptor!,
                userId: user?.name ?? '',
                title: preferitoInEdit.title,
                descriUrl: preferitoInEdit.descriUrl,
                contesto: preferitoInEdit.contesto ?? context!,
                contestoDescri: preferitoInEdit.contestoDescri,
                iconName: preferitoInEdit.iconName ?? '',
                color: preferitoInEdit.color ?? '',
                dataUltimoLancio: preferitoInEdit.dataUltimoLancio,
                url: preferitoInEdit.url,
            })
            // Se l'applicazione ha codici impostiamo il codice del contesto
            if (preferitoInEdit?.serviceDescriptor?.tipologiaSito === 'c' && preferitoInEdit.contesto) {
                // console.log(`%cImpostazione codice selezionato: ${preferitoInEdit.contesto}`, logConstants.blue12ptBold)
                setCodiceSelected({ codice: preferitoInEdit.contesto, value: preferitoInEdit.contesto, label: preferitoInEdit.contestoDescri ?? '', descrizione: preferitoInEdit.contestoDescri ?? '' })
            }
            setPrefDescri(preferitoInEdit?.descriUrl ?? '')
            // console.log('preferito setted from edit')
        }
    }, [user?.name, context, preferitoInEdit])


    const handleSetContextPreferito = useCallback((contesto: string) => {
        // console.log('contesto impostato', contesto)
        setPreferito({ ...preferito, contesto })
    }, [preferito])

    const handleSetupContext = () => {
        if (isAzienda) {
            alert('Utente azienda. Contesto non impostabile')
            return;
        }
        if (sd?.tipologiaSito === 'c') {
            if (!codiceSelected) {
                alert('Applicazione a codici, impostare il codice')
            }
            return
        }
        // console.log('inizializzo contesto open')
        // console.log('onSetContextPreferito inizializzo con', handleSetContextPreferito)
        setOpenDialog({ show: true, preferito: preferito, onSetContextPreferito: handleSetContextPreferito })
    }

    const internalHide = () => {
        setMessage(null)
        setCodiceSelected(codiceDescrizioneNullValue)
        onHide && onHide()
    }
    const handleClose = () => internalHide()
    const handleSavePreferito = () => {
        const csvalue: string | undefined = codiceSelected?.value
        //const ctx = (sd?.tipologiaSito === 'c') ? csvalue : context
        // console.log(`%cpreferito contesto: ${preferito?.contesto} withContext: ${prefWithContext}, context: ${context}, tipologiaSito: ${sd?.tipologiaSito}`
        //     , logConstants.green12ptBold)
        const ctx = (sd?.tipologiaSito === 'c') ? csvalue : (preferito?.contesto ? preferito?.contesto : context)
        // console.log('ctx', ctx)
        const codice = prefWithContext ? (ctx ?? null) : null
        if (!!!preferito.serviceDescriptor) {
            if (!preferito.url && !preferito.title && !preferito.descriUrl) {
                logger.warning(t('Dati non compilati'))
                return
            }
        }
        const sdDefault: ServiceDescriptor = {
            id: 0,
            groupDescriptorId: 0,
            url: '',
            urlDescription: '',
            livello: 'Sixtema',
            sezione: 'Link Utili',
            tipoAppl: '',
            note: null,
            serviceImageUrl: null,
            tipologiaSito: null,
            ambiente: null,
            sorter: 0,
            info: null,
            escludiProvince: null,
            perProvince: null,
            enabled: false,
            fileEncoded: '',
            disabilitato: false,
        }
        onCheckPreferito(sd ?? sdDefault, codice).then((ok) => {
            // console.log(`onCheckPreferito: ${ok.exists}`, ok)
            // console.log(`onCheckPreferito ok: ${ok.exists}`, ok)
            // console.log('PREFERITO', preferito)
            if (ok && !ok.exists) {
                // console.log(`onCheckPreferito: SAVE ${codice}`, codice, onSave)
                //sd non è più obbligatorio
                onSave && onSave(sd ?? sdDefault, prefDescri, codice, preferito)    
                internalHide()
            } else {
                const msg = codice
                    ? `${sd?.urlDescription} [con contesto: ${codice}] esiste gia'`
                    : `${sd?.urlDescription} [senza contesto] esiste gia'`
                setMessage(msg)
            }
        })
    }

    const setPreferitoUrl = (valueUrl: string) => {
        setPreferito({ ...preferito, url: valueUrl })
    }

    const setPreferitoTitle = (valueTitle: string) => {
        setPreferito({ ...preferito, title: valueTitle })
        // console.log('valueTitle', valueTitle)
    }

    const handleCloseIcons = () => {
        // setPreferito({...preferito, iconName: ''})
        setOpenIcons(false)
    }

    const handleSaveIcon = () => {
        //setPreferito({...preferito, iconName: name})
        //Semplicemente chiudo
        setOpenIcons(false)
        // console.log('preferito', preferito)
    }

    const handleSelectIcon = (name: string) => {
        //setIconSelected(true);
        // console.log('ho selezionato icona: ' + name)
        setPreferito({ ...preferito, iconName: name })
    }

    const onButtonClick = () => {
        // if (inputFile.current) {
        //     inputFile.current.click();
        //     console.log('buttonclick')
        // }
        setOpenIcons(true)
    }

    const handleOpenPopper = (event: React.MouseEvent<HTMLDivElement>) => {
        // console.log('handleOpenPopper')
        // console.log('anchorEl', anchorEl)
        setAnchorEl(anchorEl ? null : event.currentTarget);
    }

    const handleClosePopper = (event: React.MouseEvent<HTMLButtonElement>) => {
        // console.log('handleClosePopper')
        // console.log('anchorEl', anchorEl)
        setAnchorEl(null);
    }

    const handleSelectColor = (color: string) => (event: React.MouseEvent<HTMLDivElement>) => {
        // console.log('selected color', color);
        // console.log('selected target div', event.currentTarget);
        // Act
        //Close the palette color
        setPreferito({ ...preferito, color })
        // console.log('preferito', preferito)
        setAnchorEl(null);
    }

    useEffect(() => { sd && sd.tipoAppl === "Osra" && setPrefWithContext(false) }, [sd])
    // useEffect(() => {
    //     popperRefObject && console.log('popperRefObject.current', popperRefObject.current)
    // }, [anchorEl]);

    // console.log('preferito sd', preferito?.serviceDescriptor)
    const handleCodiceSelected = (option: CodiceDescrizione) => {
        setCodiceSelected(option)
    }
    return (
        <Dialog
            onClose={handleClose}
            open={open}
            maxWidth='sm'
            fullWidth
            keepMounted={true}
        >
            <MySixtemaDialogTitle onClose={onHide}>
                {title ?
                    <Typography><span>{t(title)}</span></Typography>
                    :
                    <Typography><span>{t('Aggiungi applicazione preferita')}</span></Typography>
                }
            </MySixtemaDialogTitle>
            <Box sx={{ height: 8, bgcolor: preferito?.color }}></Box>
            <Box sx={{ width: 44, height: 44, position: 'absolute', top: 54, right: 24, }}>
                {preferito?.iconName && <img alt="icona" src={`/icons/${preferito?.iconName}`} width="44" height="44"></img>}
            </Box>
            <DialogContent>
                {preferito.serviceDescriptor
                    ? <Box sx={{ display: 'flex' }}>
                        <FormLabel sx={{ width: '20%', minWidth: '20%' }}>{t('Applicazione')}:</FormLabel>
                        <b>{sd?.urlDescription ?? preferito.title}</b>
                    </Box>
                    : <Box sx={{ display: 'flex' }}>
                        <FormLabel sx={{ width: '20%', minWidth: '20%' }}>{t('Applicazione')}:</FormLabel>
                        <TextField value={preferito.title} onChange={(ev) => setPreferitoTitle(ev.currentTarget.value)} sx={{ border: '0 0 1 0', flexGrow: 1 }} variant='standard' />
                    </Box>
                }
                <Box sx={{ display: 'flex', alignItems: 'baseline', mt: '10px' }}>
                    <FormLabel sx={{ width: '20%', minWidth: '20%' }}>{t('Alias')}:</FormLabel>
                    <TextField value={prefDescri} onChange={(ev) => setPrefDescri(ev.currentTarget.value)} sx={{ border: '0 0 1 0', flexGrow: 1 }} variant='standard' />
                </Box>
                {!!!preferito.serviceDescriptor && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mt: '10px', mb: 2 }}>
                        <FormLabel sx={{ width: '20%', minWidth: '20%' }}>{t('Url')}:</FormLabel>
                        <TextField value={preferito.url} onChange={(ev) => setPreferitoUrl(ev.currentTarget.value)} sx={{ border: '0 0 1 0', flexGrow: 1 }} variant='standard' />
                    </Box>
                )}
                {prefWithContext && sd && sd.tipologiaSito === 'c' &&
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mt: '10px', mb: 2 }}>
                        <FormLabel sx={{ width: '20%', minWidth: '20%' }}>{t('Scelta Codice')}:</FormLabel>
                        <CodiciSelector variant="full" fullWidth appl={sd} onSelect={handleCodiceSelected} codiceSelected={codiceSelected} />
                    </Box>
                }
                <Box sx={{ display: preferito.serviceDescriptor ? 'flex' : 'none', flexDirection: 'row', mt: '10px', alignItems: 'center' }}>
                    <FormLabel sx={{ width: '20%', minWidth: '20%', flexGrow: 0 }}>{t('Contesto')}:</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {sd?.tipoAppl === "Osra"
                            ? <span>{t('Contesto non previsto per questa applicazione')}</span>
                            : <>
                                <Switch checked={prefWithContext} onChange={(event) => setPrefWithContext(event.target.checked)} disabled={sd?.tipoAppl === "Osra"} />
                                <label>
                                    <>
                                        {prefWithContext
                                            ? <ConContestoLabel context={preferito?.contesto && preferito?.contesto !== '' ? preferito?.contesto : context} appl={sd} codiceSelected={codiceSelected} />
                                            : <span>Senza Contesto <em>(Utilizzerà il contesto corrente all'atto del lancio)</em></span>
                                        }
                                    </>
                                </label>
                                {prefWithContext && <span onClick={handleSetupContext}><ContextIcon color='#1e88e5' /></span>}
                            </>
                        }
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px', border: 'solid 1px #c0c0c0', gap: 2, py: 1, pl: 3 }} >
                    <Box sx={{ mr: 6 }}>
                        <label>{t('Personalizza Card')}</label>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={onButtonClick} >
                        <PhotoCameraIcon sx={{ color: '#1e88e5' }} />
                        <Typography variant="body2">Icona</Typography>
                    </Box>
                    <ShowIconSelect openIcons={openIcons} iconsAll={iconsAll} preferito={preferito} handleCloseIcons={handleCloseIcons} 
                        handleSelectIcon={handleSelectIcon} handleSaveIcon={handleSaveIcon}/>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={handleOpenPopper}>
                        <PaletteIcon sx={{ color: '#1e88e5' }} />
                        <Typography variant="body2">Colore</Typography>
                    </Box>
                    <ShowColorSelect anchorEl={anchorEl} setAnchorEl={setAnchorEl} popperRefObject={popperRefObject} handleClosePopper={handleClosePopper} handleSelectColor={handleSelectColor}/>
                </Box>
                {message &&
                    <Box sx={{ bgcolor: 'rgba(241, 151, 169, 0.8)' }}>
                        <Box sx={{ padding: 1 }}>
                            {message}
                        </Box>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('Annulla')}</Button>
                <Button onClick={handleSavePreferito}>{preferito.id > 0 ? t('Modifica') : t('Aggiungi')}</Button>
            </DialogActions>
        </Dialog>
    )
}
export default SavePreferitoModal

type ConContestoLabelPropsType = {
    context?: NullableString,
    appl?: ServiceDescriptor,
    codiceSelected?: CodiceDescrizione,
}
const ConContestoLabel: React.FC<ConContestoLabelPropsType> = ({ appl, context, codiceSelected }) => {
    const label = appl?.tipologiaSito === 'c' ? codiceSelected?.value : context
    return <span><b>{label}</b></span>
}

const useIconStyles = makeStyles((theme: Theme) => createStyles({
    icon: {
        '&:hover': {
            transform: 'scale(1.2)',
            //border: 'solid 2px #1e88e5' //+ theme.palette?.primary?.main
            boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)'
        }
    }

}))

interface IconPreferitoProps {
    name: string
    preferito: Preferito | null,
    i: number
    handleSelectIcon: (name: string) => void
}

const IconPreferito: React.FC<IconPreferitoProps> = (props: IconPreferitoProps) => {
    const classes = useIconStyles()
    const { name, preferito, i } = props
    const ref = useRef<HTMLDivElement>(null)

    const handler = {
        selectIcon: () => {
            //quì chiamo la fahter function to render back the value I need to have back in the father
            // stessa cosa dell'Event emitter, devo chiamare il padre che è una props passata
            props.handleSelectIcon(name)
        }
    }

    useEffect(() => {
        if (preferito?.iconName === name && ref.current) {
            ref.current.scrollIntoView()
        }
    }, [name, preferito?.iconName])

    //'#1e88e5'
    const opacity = preferito?.iconName === name ? 0.5 : 1
    return (
        <Box sx={{ position: 'relative' }} key={i} ref={ref}>
            <img
                src={"/icons/" + name}
                style={{ padding: 1, border: preferito?.iconName === name ? 'solid 3px #404040' : 'solid 1px #c0c0c0', cursor: 'pointer', opacity }}
                width="60"
                alt="name"
                className={classes.icon}
                onClick={handler.selectIcon}
            />
            {preferito?.iconName === name &&
                <Box sx={{ color: ((theme: Theme) => theme.palette.primary.dark), position: 'absolute', top: 8, left: 8 }}>
                    <CheckIcon sx={{ width: 48, height: 48 }} />
                </Box>
            }
        </Box>
    )
}

interface ShowColorSelectProps {
    anchorEl: null | HTMLDivElement
    setAnchorEl: any
    popperRefObject: React.MutableRefObject<null>
    handleSelectColor: (color: string) => (event: React.MouseEvent<HTMLDivElement>) => void
    handleClosePopper: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const ShowColorSelect: React.FC<ShowColorSelectProps> = (props: ShowColorSelectProps) => {
    const classes = useStyles()

    return (
        <Popper
            open={Boolean(props.anchorEl)}
            anchorEl={props.anchorEl}
            ref={props.popperRefObject}
            onMouseLeave={() => props.setAnchorEl(null)}
            style={{ zIndex: '2000', width: preferitoCardColorsWidth }}
        >
            <Box sx={{ backgroundColor: '#ffffff', p: 2, pb: 1, border: 'solid 1px #c0c0c0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-around', gap: 0.5 }}>
                    {preferitoCardColors.map((x, i) => (
                        <Box
                            sx={{ bgcolor: x, p: 1, border: 'solid 1px #c0c0c0' }}
                            key={i}
                            className={classes.circle}
                            onClick={props.handleSelectColor(x)}
                        >
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', pt: 1 }}>
                    <Button onClick={props.handleClosePopper}>Annulla</Button>
                </Box>
            </Box>
        </Popper>

    )
}

interface ShowIconSelectProps {
    openIcons: boolean
    handleCloseIcons: any
    handleSelectIcon: any
    handleSaveIcon: any
    iconsAll: Loadable<string[]>
    preferito: Preferito
}
const ShowIconSelect: React.FC<ShowIconSelectProps> = (props: ShowIconSelectProps) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={props.openIcons}
            maxWidth='sm'
            fullWidth
            PaperProps={{ sx: { position: "fixed", bottom: 16, right: 16, m: 0, height: 'calc(100vh - 55%)' } }}
        >
            <MySixtemaDialogTitle onClose={props.handleCloseIcons}>
                <Typography><span>{t('Scegli icona preferito')}</span></Typography>
            </MySixtemaDialogTitle>
            <DialogContent >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 0.5, overflow: 'auto' }}>
                    {props.iconsAll.state === 'hasValue' && props.iconsAll.contents &&
                        props.iconsAll.contents.map((name: string, i: number) => (
                            <IconPreferito key={name} name={name} i={i} handleSelectIcon={props.handleSelectIcon} preferito={props.preferito} />
                        ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.handleCloseIcons}>Annulla</Button>
                <Button onClick={props.handleSaveIcon}>Salva</Button>
            </DialogActions>
        </Dialog>
    )
}
